import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface VoteBarProps {
  debateId: string;
  onVote: (side: 'A' | 'B', metrics?: { logic: number; clarity: number; evidence: number }) => void;
  disabled?: boolean;
  hasVoted?: boolean;
  userVote?: 'pro' | 'con' | null;
  isAuthenticated?: boolean;
}

const VoteBar: React.FC<VoteBarProps> = ({
  debateId,
  onVote,
  disabled = false,
  hasVoted = false,
  userVote = null,
  isAuthenticated = false,
}) => {
  const [selectedSide, setSelectedSide] = useState<'A' | 'B' | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Set selected side based on existing vote
  useEffect(() => {
    if (hasVoted && userVote) {
      setSelectedSide(userVote === 'pro' ? 'A' : 'B');
    }
  }, [hasVoted, userVote]);

  const handleVote = async () => {
    if (selectedSide && !isSubmitting) {
      setIsSubmitting(true);
      await onVote(selectedSide);
      setIsSubmitting(false);
    }
  };

  return (
    <div className="sticky bottom-0 z-40 border-t border-black/[0.08] bg-white/90 backdrop-blur-md">
      <div className="mx-auto max-w-5xl px-4 py-4 sm:px-6 lg:px-8">
        <div className="flex flex-col gap-3">
          {/* Status line */}
          <div className="flex items-center justify-center gap-2 text-center">
            {hasVoted ? (
              <motion.span
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                className="inline-flex items-center gap-2 rounded-full border border-black/[0.08] bg-white px-3 py-1 font-mono text-[11px] uppercase tracking-wider text-charcoal"
              >
                <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                </svg>
                Vote recorded · tap a side to change it
              </motion.span>
            ) : (
              <span className="font-mono text-[11px] uppercase tracking-eyebrow text-ink-muted">
                Which side argued it better?
              </span>
            )}
          </div>

          {/* Main voting buttons — For left, Against right */}
          <div className="grid w-full grid-cols-2 gap-3 sm:gap-4">
            <motion.button
              whileTap={{ scale: disabled || isSubmitting ? 1 : 0.98 }}
              onClick={() => !disabled && setSelectedSide('A')}
              disabled={disabled || isSubmitting}
              className={`group relative overflow-hidden rounded-xl border px-4 py-3 text-left transition-all duration-300 ease-out-expo disabled:cursor-not-allowed disabled:opacity-50 ${
                selectedSide === 'A'
                  ? 'border-steel-blue bg-steel-blue text-white shadow-lift'
                  : 'border-steel-blue/30 bg-white text-steel-blue hover:border-steel-blue hover:bg-steel-blue/[0.06]'
              }`}
            >
              <span className="font-mono text-[10px] uppercase tracking-wider opacity-70">For</span>
              <span className="mt-0.5 block text-sm font-semibold">
                {selectedSide === 'A' ? '✓ Side A stronger' : 'Side A stronger'}
              </span>
            </motion.button>

            <motion.button
              whileTap={{ scale: disabled || isSubmitting ? 1 : 0.98 }}
              onClick={() => !disabled && setSelectedSide('B')}
              disabled={disabled || isSubmitting}
              className={`group relative overflow-hidden rounded-xl border px-4 py-3 text-right transition-all duration-300 ease-out-expo disabled:cursor-not-allowed disabled:opacity-50 ${
                selectedSide === 'B'
                  ? 'border-vermilion bg-vermilion text-white shadow-lift'
                  : 'border-vermilion/30 bg-white text-vermilion hover:border-vermilion hover:bg-vermilion/[0.06]'
              }`}
            >
              <span className="font-mono text-[10px] uppercase tracking-wider opacity-70">Against</span>
              <span className="mt-0.5 block text-sm font-semibold">
                {selectedSide === 'B' ? 'Side B stronger ✓' : 'Side B stronger'}
              </span>
            </motion.button>
          </div>

          {/* Sign in message */}
          {!isAuthenticated && (
            <p className="text-center text-xs text-ink-muted">Sign in to vote on this debate</p>
          )}

          {/* Submit button */}
          <AnimatePresence>
            {selectedSide && !hasVoted && (
              <motion.div
                initial={{ opacity: 0, y: 10, height: 0 }}
                animate={{ opacity: 1, y: 0, height: 'auto' }}
                exit={{ opacity: 0, y: 10, height: 0 }}
                className="flex justify-center overflow-hidden"
              >
                <button
                  onClick={handleVote}
                  disabled={disabled || isSubmitting || !isAuthenticated}
                  className="btn-primary min-w-[180px] !py-2.5 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:translate-y-0 disabled:hover:shadow-none"
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Submitting…
                    </span>
                  ) : (
                    'Submit vote'
                  )}
                </button>
              </motion.div>
            )}
            {selectedSide && hasVoted && selectedSide !== (userVote === 'pro' ? 'A' : 'B') && (
              <motion.div
                initial={{ opacity: 0, y: 10, height: 0 }}
                animate={{ opacity: 1, y: 0, height: 'auto' }}
                exit={{ opacity: 0, y: 10, height: 0 }}
                className="flex justify-center overflow-hidden"
              >
                <button
                  onClick={handleVote}
                  disabled={disabled || isSubmitting || !isAuthenticated}
                  className="btn-primary min-w-[180px] !py-2.5 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  {isSubmitting ? 'Submitting…' : 'Update vote'}
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default VoteBar;
